import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiIcon,
  MiPlaceholderArea,
  MiSearchInput,
} from "@miplatform/components";
import { useRouter } from "@miplatform/hooks";
import { mdiMagnify } from "@mdi/js";
import { SidebarLayout } from "@mibuildweb/components";
import { InputAdornment } from "@mui/material";
import { Box } from "@mui/system";
import { HomeFilterSidebar } from "./components/HomeFilterSidebar";
import { HomeList } from "./components/HomeList";
import { HomesPageHeader } from "./components/HomesPageHeader";
import { useHomeFilters } from "./hooks/useHomeFilters";
import { useHomesViewModel } from "./hooks/useHomesViewModel";
import { GetHomesParams } from "./types";

export function HomesList() {
  const { query, navigate } = useRouter();
  const { classes, theme } = useStyles();

  const {
    homesListData,
    isLoading,
    getHomes,
    search,
    setSearch,
    setHomesListData,
  } = useHomesViewModel();

  const {
    filters,
    orderTemplateOptions,
    communityOptions,
    builderOptions,
    homePhaseOptions,
    handleFilterApply,
    getCommunities,
    getBuilders,
    getOrderTemplateOptions,
    setSearchParams,
    isLoading: isFiltersLoading,
  } = useHomeFilters();

  const dataProvider = (params: GetHomesParams) =>
    getHomes({ ...params, filters });

  const resetFilters = () => {
    navigate("/homes");
  };

  const handleListSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    setSearch(value);
    setSearchParams((prevParams) => {
      if (value) {
        prevParams.set("search", value);
      } else {
        prevParams.delete("search");
      }
      return prevParams;
    });
  };

  useEffect(() => {
    Promise.allSettled([
      getBuilders(),
      getCommunities(),
      getOrderTemplateOptions(),
    ]);
  }, []);

  useEffect(() => {
    if (query.search && typeof query.search === "string") {
      setSearch(query.search);
    }
  }, [query.search]);

  return (
    <>
      <HomesPageHeader isLoading={isLoading} />
      <SidebarLayout
        sidebarContent={
          <HomeFilterSidebar
            customerOptions={builderOptions}
            communityOptions={communityOptions}
            phaseOptions={homePhaseOptions}
            filters={filters}
            onApplyFilters={handleFilterApply}
            resetFilters={resetFilters}
            orderTemplateOptions={orderTemplateOptions}
            loading={isFiltersLoading || isLoading}
          />
        }
      >
        <Box className={classes.searchContainerFiltered}>
          <MiPlaceholderArea
            showLogo
            containerStyle={{
              backgroundColor: theme.palette.common.white,
              height: "auto",
            }}
            logoStyle={{ height: 32 }}
          />
          <MiSearchInput
            label="Search Homes"
            value={search}
            handleChange={handleListSearch}
            data-testid="homes-search-input"
            className={classes.searchInput}
            textfieldProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MiIcon
                    path={mdiMagnify}
                    size={0.8}
                    color={theme.palette.text.secondary}
                  />
                </InputAdornment>
              ),
            }}
            shrink
          />
        </Box>
        <Box className={classes.homeListWrapper}>
          <HomeList
            data={homesListData}
            setData={setHomesListData}
            dataProvider={dataProvider}
            filters={filters}
            search={search}
          />
        </Box>
      </SidebarLayout>
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  searchContainerFiltered: {
    flex: 0,
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "center",
    marginTop: 0,
  },
  searchInput: {
    width: "30%",
  },
  homeListWrapper: {
    flex: 1,
    overflowY: "auto",
  },
}));
